import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '../components/page-action-bar/page-action-bar';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  ICountBucket,
  IStockItem,
  IStockLocation,
  IStockTake,
  IStockTakeLine,
  IStockTakeAdjustmentSummary,
  IStockTakeLineFailure,
  StockTakeAnomalyFlag,
  StockTakeReasonHint,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Anomaly flag → human-readable label (chip text + tooltip).
const ANOMALY_LABEL: Record<
  StockTakeAnomalyFlag,
  { short: string; long: string }
> = {
  result_negative: {
    short: 'Negative',
    long: 'Counted below zero — physically impossible. Recount or explain (e.g. double-recorded sale).',
  },
  large_variance: {
    short: 'Big swing',
    long: 'Difference from expected is over the shop variance threshold. Recount or explain.',
  },
  large_loss_value: {
    short: 'Big loss',
    long: 'Negative variance valued above the shop loss threshold. Recount or explain.',
  },
  skipped_with_activity: {
    short: 'Skipped',
    long: 'Line was uncounted but the item had stock activity since the last take. Enter a count.',
  },
  overnight_drift: {
    short: 'Drift',
    long: 'Opening count differs from prior closing count. Explain (theft, overnight spoilage, etc.).',
  },
};

const REASON_HINTS: Array<{ value: StockTakeReasonHint; label: string }> = [
  { value: 'breakage', label: 'Breakage' },
  { value: 'spoilage', label: 'Spoilage' },
  { value: 'theft', label: 'Theft' },
  { value: 'miscount', label: 'Miscount' },
  { value: 'overnight_drift', label: 'Overnight drift' },
  { value: 'other', label: 'Other' },
];

const PHONE_MAX_PX = 720;

// Stocktake editor — viewport-adaptive. The same data model + state powers
// both layouts:
//
//   * Phone (≤720px): a compact one-line-per-item list grouped by walk-order
//     location. Tap a row → opens a full-page-on-phone detail dialog with
//     the multi-bucket editor, No-Stock shortcut, and the reason+explanation
//     for anomalous lines. Filling a row's count returns to the list with
//     a tick — the satisfying "I did that one" feedback the operator wants
//     while walking the shop.
//
//   * Tablet/desktop (>720px): a dense single-row table per item, inline-
//     editable for the common single-bucket case (qty + unit pill). The
//     rare multi-bucket case (whole milk in the sample data: 2L + 600ml)
//     shows a compact summary + Edit button that opens the SAME detail
//     dialog — so the multi-bucket UI lives in one place, not duplicated.
//     Anomaly explanations open via a small inline button rather than a
//     permanent full-width row, keeping every row at one line.
//
// Sticky "List / Report" tab group at the top switches between the editor
// above and a read-only summary (counted/uncounted, lines needing
// explanation as tap-to-open targets, per-location progress) — same
// content shape on both viewports.
@customElement('stocktake-edit-page')
export class StocktakeEditPage extends Base {
  @state() private _take: IStockTake | null = null;
  @state() private _itemsById = new Map<string, IStockItem>();
  @state() private _locations: IStockLocation[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _busy = false;
  @state() private _summary: IStockTakeAdjustmentSummary | null = null;
  @state() private _lineFailures: IStockTakeLineFailure[] = [];

  // Per-line UI drafts. Server is the source of truth for committed state;
  // these track in-flight typing so the operator can blur to commit. The
  // bucket draft is a deep copy of line.counts; mutations stay local until
  // the operator blurs out of the bucket row OR taps a fast-action chip.
  @state() private _bucketDrafts = new Map<string, ICountBucket[]>();
  @state() private _expDrafts = new Map<string, string>();
  @state() private _hintDrafts = new Map<string, StockTakeReasonHint | ''>();

  // View state.
  @state() private _viewMode: 'list' | 'report' = 'list';
  @state() private _detailForId: string | null = null;
  @state() private _isPhone = false;

  private _phoneMql: MediaQueryList | null = null;
  private _phoneListener = () => {
    this._isPhone = !!this._phoneMql?.matches;
  };

  // Helper — resolved baseUnits for a bucket list. Mirrors the server's
  // ResolvedCount(). Used everywhere the UI displays a "counted" total.
  private static _resolve(buckets: ICountBucket[]): number {
    return buckets.reduce(
      (sum, b) => sum + b.quantity * b.unitSize * b.fillFraction,
      0
    );
  }

  // Default starting bucket list for a line. If the line already has saved
  // counts, return a deep copy. Otherwise default to a single bucket sized
  // in the item's baseUnit ("raw mode") — the cashier can switch to a
  // pack-size via the per-bucket unit picker.
  private _defaultBuckets(line: IStockTakeLine): ICountBucket[] {
    if (line.counts.length > 0) {
      return line.counts.map((b) => ({ ...b }));
    }
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    return [
      {
        quantity: 0,
        unitSize: 1,
        unitLabel: baseUnit,
        fillFraction: 1,
        method: 'estimated',
      },
    ];
  }

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 1200px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      @media (max-width: 720px) {
        :host {
          padding: 12px 10px;
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
        }
      }

      /* === Page header — editorial display ===
         The page-level H2 carries the design POV: Bricolage display,
         oversized, tracked tight. Below it a single mono metadata
         line lays out kind / status / progress like a control-panel
         masthead. */
      header.bar {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        margin-bottom: 4px;
        gap: 16px;
        flex-wrap: wrap;
      }
      header.bar h2 {
        margin: 0;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: clamp(1.6rem, 4vw, 2.4rem);
        letter-spacing: -0.02em;
        line-height: 1;
        text-transform: uppercase;
      }
      .header-meta {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
      }
      .header-meta sl-badge::part(base) {
        font-family: var(--font-mono);
        font-weight: 500;
        letter-spacing: 0.04em;
      }
      p.subtle {
        margin: 0 0 14px 0;
        font-family: var(--font-mono);
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-size: 0.78rem;
        letter-spacing: 0.02em;
      }
      p.subtle strong {
        color: var(--app-text);
        font-weight: 600;
      }

      /* Sticky view-mode tabs — mono labels for the control-panel feel. */
      sl-tab-group.view-tabs {
        position: sticky;
        top: 0;
        z-index: 5;
        background: var(--app-bg);
        margin: 0 -4px 14px -4px;
        padding: 0 4px;
      }
      sl-tab-group.view-tabs::part(base) {
        border-bottom: 1px solid var(--app-border);
      }
      sl-tab-group.view-tabs::part(body) {
        padding: 0;
      }
      sl-tab-group.view-tabs sl-tab::part(base) {
        font-family: var(--font-mono);
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        font-size: 0.78rem;
      }

      /* === Location group header — editorial ruled head ===
         Title sits left, a hairline rule fills the gap, the counted/total
         pinned right in mono. Visually breaks the page like a section
         divider in a print magazine. */
      .loc-header {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 12px;
        margin: 24px 0 10px 0;
      }
      .loc-header:first-child {
        margin-top: 0;
      }
      .loc-header h3 {
        margin: 0;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 0.9rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--app-text);
      }
      .loc-header .loc-rule {
        height: 1px;
        background: var(--app-border);
      }
      .loc-header .loc-count {
        font-family: var(--font-mono);
        font-size: 0.75rem;
        font-weight: 500;
        letter-spacing: 0.06em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-variant-numeric: tabular-nums;
      }
      .loc-header .loc-count strong {
        color: var(--app-text);
        font-weight: 700;
      }

      /* === Phone list rows ===
         Each row is a tap target. Item name in editorial body weight;
         resolved count in mono with tabular nums — the operator's eye
         scans the right-aligned status icon for the "did I count this"
         signal. */
      .phone-rows {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      button.phone-row {
        all: unset;
        cursor: pointer;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 12px;
        align-items: center;
        padding: 12px 14px;
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--sl-border-radius-medium);
        min-height: 60px;
        box-sizing: border-box;
        transition:
          background 0.15s ease,
          transform 0.1s ease;
      }
      button.phone-row:hover {
        background: var(--sl-color-neutral-50);
      }
      .sl-theme-dark button.phone-row:hover {
        background: var(--sl-color-neutral-900);
      }
      button.phone-row:active {
        transform: scale(0.995);
      }
      button.phone-row.flagged {
        border-left: 3px solid var(--sl-color-warning-600);
        background: var(--sl-color-warning-50);
      }
      .sl-theme-dark button.phone-row.flagged {
        background: rgba(245, 158, 11, 0.08);
      }
      .phone-row .row-name {
        font-family: var(--font-display);
        font-weight: 600;
        font-size: 1rem;
        line-height: 1.2;
        letter-spacing: -0.01em;
        color: var(--app-text);
      }
      .phone-row .row-sub {
        margin-top: 4px;
        font-family: var(--font-mono);
        font-size: 0.78rem;
        font-weight: 500;
        letter-spacing: 0.01em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
      }
      .phone-row .row-sub .pos {
        color: var(--sl-color-success-600);
        font-weight: 600;
      }
      .phone-row .row-sub .neg {
        color: var(--sl-color-warning-700);
        font-weight: 600;
      }
      .sl-theme-dark .phone-row .row-sub .neg {
        color: var(--sl-color-warning-500);
      }
      .phone-row .row-status {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .phone-row .row-status sl-icon {
        font-size: 1.5rem;
      }
      .phone-row .row-status .check {
        color: var(--sl-color-success-600);
      }
      .phone-row .row-status .warn {
        color: var(--sl-color-warning-600);
      }
      .phone-row .row-status .empty {
        color: var(--sl-color-neutral-400);
      }

      /* === Tablet/desktop dense table === */
      .table-head,
      .table-row {
        display: grid;
        grid-template-columns:
          minmax(220px, 1.5fr) /* Item */
          80px /* Expected */
          minmax(200px, 1.3fr) /* Counted */
          80px /* Variance */
          80px /* Loss */
          minmax(140px, auto); /* Anomaly */
        gap: 12px;
        align-items: center;
        padding: 8px 14px;
      }
      .table-head {
        font-family: var(--font-mono);
        font-weight: 600;
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        border-bottom: 1px solid var(--app-border);
        padding-bottom: 10px;
      }
      .table-row {
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--sl-border-radius-medium);
        min-height: 48px;
        transition: background 0.15s ease;
      }
      .table-row:hover {
        background: var(--sl-color-neutral-50);
      }
      .sl-theme-dark .table-row:hover {
        background: var(--sl-color-neutral-900);
      }
      .table-row.flagged {
        background: var(--sl-color-warning-50);
        border-left: 3px solid var(--sl-color-warning-600);
        padding-left: 11px;
      }
      .sl-theme-dark .table-row.flagged {
        background: rgba(245, 158, 11, 0.08);
      }
      .table-rows {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .cell-item {
        font-family: var(--font-display);
        font-weight: 600;
        font-size: 0.95rem;
        letter-spacing: -0.01em;
        display: flex;
        align-items: baseline;
        gap: 8px;
        min-width: 0;
      }
      .cell-item small {
        font-family: var(--font-mono);
        font-weight: 500;
        opacity: 0.55;
        font-size: 0.7rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .num {
        text-align: right;
        font-family: var(--font-mono);
        font-variant-numeric: tabular-nums;
        font-weight: 500;
      }
      .pos {
        color: var(--sl-color-success-700);
      }
      .neg {
        color: var(--sl-color-danger-700);
      }
      .sl-theme-dark .pos {
        color: var(--sl-color-success-500);
      }
      .sl-theme-dark .neg {
        color: var(--sl-color-warning-500);
      }

      /* Counted cell — single-bucket inline editor */
      .counted-inline {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
      }
      .counted-inline sl-input {
        width: 80px;
        flex-shrink: 0;
      }
      .counted-inline sl-select {
        min-width: 0;
        flex: 1;
      }
      .counted-inline .fill {
        width: 64px;
        flex-shrink: 0;
      }
      .counted-inline .no-stock-btn {
        flex-shrink: 0;
      }
      .counted-summary {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
      }
      .counted-summary .summary-pills {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        flex: 1;
        min-width: 0;
      }
      .counted-summary .pill {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        font-weight: 500;
        letter-spacing: 0.02em;
        padding: 2px 8px;
        background: var(--sl-color-neutral-200);
        color: var(--app-text);
        border-radius: 2px;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .sl-theme-dark .counted-summary .pill {
        background: var(--sl-color-neutral-700);
      }

      .anomaly-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
      }
      .anomaly-cell sl-tag::part(base) {
        font-family: var(--font-mono);
        font-size: 0.68rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      /* === Detail dialog ===
         Item name in the dialog's slotted label becomes a Bricolage
         display head (see ::part(title) below). Header line below it
         is a location pill + a single confident "Expected N each"
         sentence — no label/value ceremony. */
      sl-dialog.detail-dlg::part(panel) {
        background: var(--app-surface);
      }
      sl-dialog.detail-dlg::part(title) {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.5rem;
        letter-spacing: -0.02em;
      }
      sl-dialog.detail-dlg::part(body) {
        padding-top: 14px;
      }
      .detail-head {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 18px;
      }
      .detail-head .loc-pill {
        font-family: var(--font-mono);
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 4px 10px;
        background: var(--app-text);
        color: var(--app-surface);
        border-radius: 2px;
      }
      .detail-head .expected {
        font-family: var(--font-mono);
        font-size: 0.78rem;
        font-weight: 500;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      .detail-head .expected strong {
        color: var(--app-text);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      /* === Variance card — the page's visual anchor ===
         Two oversized mono numerals separated by a hairline rule. Labels
         are tight uppercase mono whispers above; the figures dominate.
         Numbers fade-up on first paint to draw the eye on dialog open. */
      .detail-variance {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0;
        padding: 22px 20px;
        background: var(--app-bg);
        border: 1px solid var(--app-border);
        border-radius: var(--sl-border-radius-medium);
        margin-bottom: 22px;
        position: relative;
      }
      .sl-theme-dark .detail-variance {
        background: rgba(255, 255, 255, 0.02);
      }
      .detail-variance .pair {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 0 16px;
        border-right: 1px solid var(--app-border);
      }
      .detail-variance .pair:first-child {
        padding-left: 0;
      }
      .detail-variance .pair:last-child {
        border-right: none;
        padding-right: 0;
      }
      .detail-variance .label {
        font-family: var(--font-mono);
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-500));
      }
      .detail-variance .value {
        font-family: var(--font-mono);
        font-size: clamp(2rem, 8vw, 2.8rem);
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        color: var(--app-text);
        animation: variance-reveal 0.45s cubic-bezier(0.2, 0.7, 0.2, 1) both;
      }
      .detail-variance .value small {
        font-family: var(--font-mono);
        font-size: 0.32em;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        opacity: 0.55;
        margin-left: 8px;
        vertical-align: 0.4em;
      }
      @keyframes variance-reveal {
        from {
          opacity: 0;
          transform: translateY(6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .detail-variance .value {
          animation: none;
        }
      }

      .bucket-editor {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .bucket-row {
        display: grid;
        grid-template-columns: 100px 14px 1fr 80px 36px;
        gap: 8px;
        align-items: center;
      }
      .bucket-row.single-unit {
        grid-template-columns: 100px auto 1fr 80px 36px;
      }
      .bucket-row sl-select {
        min-width: 0;
      }
      .bucket-row .bucket-x {
        text-align: center;
        opacity: 0.55;
        font-size: 1.1rem;
      }
      .bucket-row .static-unit {
        font-weight: 500;
        color: var(--app-text);
      }
      .bucket-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 6px;
      }
      .recon {
        margin-top: 14px;
        padding-top: 12px;
        border-top: 1px dashed var(--sl-color-neutral-300);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .recon h4 {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 600;
      }
      .recon-row {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 8px;
      }
      @media (max-width: 720px) {
        .recon-row {
          grid-template-columns: 1fr;
        }
      }

      /* === Report view ===
         Stats grid uses the same typographic system as the variance
         card — big mono numbers, tight uppercase mono labels. Section
         heads in Bricolage display so the rhythm matches list view. */
      .report-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 12px;
        margin-bottom: 16px;
      }
      .stat {
        padding: 16px 18px;
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: var(--sl-border-radius-medium);
      }
      .stat .label {
        font-family: var(--font-mono);
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      .stat .value {
        font-family: var(--font-mono);
        font-size: 2rem;
        font-weight: 700;
        line-height: 1.1;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        margin-top: 8px;
      }
      .stat .value small {
        font-family: var(--font-mono);
        font-size: 0.45em;
        font-weight: 500;
        letter-spacing: 0.04em;
        opacity: 0.55;
        margin-left: 4px;
      }
      .report-section h3 {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 0.9rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        margin: 20px 0 10px 0;
      }
      .anomaly-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .anomaly-list button {
        all: unset;
        cursor: pointer;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 12px;
        padding: 14px 16px;
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-warning-50);
        border-left: 3px solid var(--sl-color-warning-600);
        transition:
          transform 0.1s ease,
          background 0.15s ease;
      }
      .anomaly-list button:hover {
        background: var(--sl-color-warning-100);
      }
      .sl-theme-dark .anomaly-list button {
        background: rgba(245, 158, 11, 0.08);
      }
      .sl-theme-dark .anomaly-list button:hover {
        background: rgba(245, 158, 11, 0.14);
      }
      .anomaly-list .a-name {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 0.95rem;
        letter-spacing: -0.01em;
      }
      .anomaly-list .a-meta {
        font-family: var(--font-mono);
        font-size: 0.74rem;
        font-weight: 500;
        letter-spacing: 0.02em;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        margin-top: 4px;
        font-variant-numeric: tabular-nums;
      }
      .anomaly-list .a-cta {
        align-self: center;
        font-family: var(--font-mono);
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--sl-color-warning-700);
      }
      .sl-theme-dark .anomaly-list .a-cta {
        color: var(--sl-color-warning-500);
      }
      .by-loc {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      .by-loc-row {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
        padding: 12px 4px;
        border-bottom: 1px solid var(--app-border);
        font-family: var(--font-mono);
        font-size: 0.85rem;
      }
      .by-loc-row:last-child {
        border-bottom: none;
      }
      .by-loc-row > span:first-child {
        font-family: var(--font-display);
        font-weight: 600;
        font-size: 0.85rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .by-loc-row > span:last-child {
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.04em;
      }
      .by-loc-row strong {
        font-weight: 700;
      }

      .summary {
        margin-top: 16px;
      }
      .line-failures {
        margin: 8px 0 0;
        padding-left: 18px;
      }
      .line-failures li {
        margin-bottom: 6px;
      }
      .line-failure-kind {
        display: inline-block;
        margin-left: 6px;
        font-size: 0.78rem;
        color: var(--sl-color-warning-800);
        background: var(--sl-color-warning-100);
        padding: 1px 6px;
        border-radius: 999px;
      }
      .line-failure-message {
        font-size: 0.85rem;
        opacity: 0.85;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this._phoneMql = window.matchMedia(`(max-width: ${PHONE_MAX_PX}px)`);
    this._isPhone = this._phoneMql.matches;
    this._phoneMql.addEventListener('change', this._phoneListener);
  }

  disconnectedCallback(): void {
    this._phoneMql?.removeEventListener('change', this._phoneListener);
    this._phoneMql = null;
    super.disconnectedCallback();
  }

  firstUpdated() {
    void this._load();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) void this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    const id = readTakeIdFromPath();
    if (!id) {
      this._error = 'No stock take id in URL.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [takeR, stockR, locR] = await Promise.all([
      api.getStockTake(this.activeShop.shopName, id),
      api.listStock(this.activeShop.shopName),
      api.listStockLocations(this.activeShop.shopName),
    ]);
    if (!takeR.success || !takeR.data) {
      this._error = takeR.error ?? 'Stock take not found.';
      this._loading = false;
      return;
    }
    this._take = takeR.data;
    if (stockR.success && stockR.data) {
      this._itemsById = new Map(stockR.data.map((i) => [i.id, i]));
    }
    if (locR.success && locR.data) this._locations = locR.data;
    this._refreshDraftsFromTake();
    this._loading = false;
  }

  private _refreshDraftsFromTake() {
    if (!this._take) return;
    this._bucketDrafts = new Map(
      this._take.lines.map((l) => [l.stockItemId, this._defaultBuckets(l)])
    );
    this._expDrafts = new Map(
      this._take.lines.map((l) => [
        l.stockItemId,
        l.reconciliationExplanation ?? '',
      ])
    );
    this._hintDrafts = new Map(
      this._take.lines.map((l) => [
        l.stockItemId,
        (l.reasonHint ?? '') as StockTakeReasonHint | '',
      ])
    );
  }

  // Commit the line's current bucket draft to the server. Empty bucket
  // list = explicit skip (clears Counts back to empty). One-or-more
  // buckets = counted; the server resolves to baseUnits.
  private async _saveBuckets(line: IStockTakeLine, buckets: ICountBucket[]) {
    if (!this.activeShop || !this._take) return;
    this._error = null;
    const r = await ApiFactory.authenticated().recordStockTakeCount(
      this.activeShop.shopName,
      this._take.id,
      line.stockItemId,
      { counts: buckets }
    );
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to save count.';
      return;
    }
    this._take = r.data;
    this._refreshDraftsFromTake();
  }

  // Fast action — explicit "we counted zero of this." Writes a single
  // zero-quantity bucket sized in the item's baseUnit. Distinct from
  // skip (empty Counts): skipped_with_activity won't fire on this.
  private async _noStock(line: IStockTakeLine) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    await this._saveBuckets(line, [
      {
        quantity: 0,
        unitSize: 1,
        unitLabel: baseUnit,
        fillFraction: 1,
        method: 'estimated',
      },
    ]);
  }

  // Mutate a single field on a single bucket within a line's draft.
  // Local-only — does NOT auto-save. Caller decides when to flush via
  // _saveBuckets on blur / Enter / chip tap.
  private _setBucketField<K extends keyof ICountBucket>(
    stockItemId: string,
    bucketIndex: number,
    field: K,
    value: ICountBucket[K]
  ) {
    const cur = this._bucketDrafts.get(stockItemId) ?? [];
    const next = cur.map((b, i) =>
      i === bucketIndex ? { ...b, [field]: value } : b
    );
    this._bucketDrafts = new Map(this._bucketDrafts).set(stockItemId, next);
  }

  private _addBucketRow(line: IStockTakeLine) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    const cur = this._bucketDrafts.get(line.stockItemId) ?? [];
    this._bucketDrafts = new Map(this._bucketDrafts).set(line.stockItemId, [
      ...cur,
      {
        quantity: 0,
        unitSize: 1,
        unitLabel: baseUnit,
        fillFraction: 1,
        method: 'estimated',
      },
    ]);
  }

  private _removeBucketRow(stockItemId: string, bucketIndex: number) {
    const cur = this._bucketDrafts.get(stockItemId) ?? [];
    const next = cur.filter((_, i) => i !== bucketIndex);
    this._bucketDrafts = new Map(this._bucketDrafts).set(stockItemId, next);
  }

  // Bucket unit picker: changing the selected size flips unitSize +
  // unitLabel together so they stay consistent.
  private _setBucketUnit(
    stockItemId: string,
    bucketIndex: number,
    unitSize: number,
    unitLabel: string
  ) {
    const cur = this._bucketDrafts.get(stockItemId) ?? [];
    const next = cur.map((b, i) =>
      i === bucketIndex ? { ...b, unitSize, unitLabel } : b
    );
    this._bucketDrafts = new Map(this._bucketDrafts).set(stockItemId, next);
  }

  private async _saveReconciliation(
    line: IStockTakeLine,
    explanation: string,
    hint: StockTakeReasonHint | ''
  ) {
    if (!this.activeShop || !this._take) return;
    this._error = null;
    const r = await ApiFactory.authenticated().recordStockTakeCount(
      this.activeShop.shopName,
      this._take.id,
      line.stockItemId,
      {
        counts: line.counts,
        reconciliationExplanation: explanation.trim() || undefined,
        reasonHint: hint || undefined,
      }
    );
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to save reconciliation.';
      return;
    }
    this._take = r.data;
    this._refreshDraftsFromTake();
  }

  private async _complete() {
    if (!this.activeShop || !this._take || this._busy) return;
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().completeStockTake(
      this.activeShop.shopName,
      this._take.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to complete stock take.';
      await this._load();
      return;
    }
    this._take = r.data.take;
    this._summary = r.data.summary;
    this._lineFailures = r.data.lineFailures ?? [];
    this._viewMode = 'report';
  }

  private async _cancel() {
    if (!this.activeShop || !this._take || this._busy) return;
    if (
      !confirm('Cancel this stock take? All recorded counts will be discarded.')
    )
      return;
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().cancelStockTake(
      this.activeShop.shopName,
      this._take.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to cancel.';
      return;
    }
    this._take = r.data;
  }

  private _back() {
    void router.navigate(resolveRouterPath('stocktake'));
  }

  private _openDetail(stockItemId: string) {
    this._detailForId = stockItemId;
  }

  private _closeDetail() {
    this._detailForId = null;
  }

  private _failureKindLabel(kind: string): string {
    switch (kind) {
      case 'adjustment-skipped':
        return 'Adjustment skipped';
      case 'audit-movement-missing':
        return 'Audit row missing';
      case 'stock-item-missing':
        return 'Item no longer exists';
      default:
        return kind;
    }
  }

  // Lines that are flagged AND lack an explanation block Complete server-side.
  private _unreconciledLines(): IStockTakeLine[] {
    if (!this._take) return [];
    return this._take.lines.filter(
      (l) => l.anomalyFlags.length > 0 && !l.reconciliationExplanation
    );
  }

  private _kindBadge(kind: string) {
    const variant =
      kind === 'opening'
        ? 'warning'
        : kind === 'closing'
          ? 'primary'
          : 'neutral';
    return html`<sl-badge variant=${variant} pill>${kind}</sl-badge>`;
  }

  // K.2 — group lines by location in SortOrder. Items without a
  // location (or with a stale LocationId pointing at a now-deleted
  // location) fall through to an "Unsorted" group rendered last.
  private _groupLinesByLocation(): Array<{
    key: string;
    label: string;
    lines: IStockTakeLine[];
  }> {
    if (!this._take) return [];
    const locById = new Map(this._locations.map((l) => [l.id, l]));
    const groups = new Map<
      string,
      { label: string; sortOrder: number; lines: IStockTakeLine[] }
    >();
    const unsortedKey = '__unsorted__';
    const unsortedGroup = {
      label: 'Unsorted',
      sortOrder: Number.MAX_SAFE_INTEGER,
      lines: [] as IStockTakeLine[],
    };
    groups.set(unsortedKey, unsortedGroup);
    for (const line of this._take.lines) {
      const item = this._itemsById.get(line.stockItemId);
      const locId = item?.locationId ?? null;
      const loc = locId ? locById.get(locId) : null;
      if (!loc || !loc.isActive) {
        unsortedGroup.lines.push(line);
        continue;
      }
      const key = loc.id;
      let group = groups.get(key);
      if (!group) {
        group = {
          label: loc.name,
          sortOrder: loc.sortOrder,
          lines: [],
        };
        groups.set(key, group);
      }
      group.lines.push(line);
    }
    return [...groups.entries()]
      .filter(([, g]) => g.lines.length > 0)
      .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
      .map(([key, g]) => ({ key, label: g.label, lines: g.lines }));
  }

  // ---------- Phone list ----------
  private _renderPhoneList() {
    const groups = this._groupLinesByLocation();
    const usingLocations = this._locations.length > 0;
    if (!usingLocations) {
      return html`<div class="phone-rows">
        ${this._take?.lines.map((l) => this._renderPhoneRow(l))}
      </div>`;
    }
    return html`
      ${groups.map((g) => {
        const cnt = g.lines.filter((l) => l.counts.length > 0).length;
        return html`
          <header class="loc-header">
            <h3>${g.label}</h3>
            <span class="loc-rule"></span>
            <span class="loc-count"
              ><strong>${cnt}</strong> / ${g.lines.length}</span
            >
          </header>
          <div class="phone-rows">
            ${g.lines.map((l) => this._renderPhoneRow(l))}
          </div>
        `;
      })}
    `;
  }

  private _renderPhoneRow(line: IStockTakeLine) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    const counted =
      line.counts.length > 0 ? StocktakeEditPage._resolve(line.counts) : null;
    const variance = counted != null ? counted - line.expectedQty : null;
    const flagged = line.anomalyFlags.length > 0;
    return html`
      <button
        class="phone-row ${flagged ? 'flagged' : ''}"
        @click=${() => this._openDetail(line.stockItemId)}
      >
        <div>
          <div class="row-name">${item?.name ?? line.stockItemId}</div>
          <div class="row-sub">
            ${
              counted != null
                ? html`
                    ${counted} ${baseUnit}
                    ${
                      variance != null && variance !== 0
                        ? html`
                            <span class=${variance > 0 ? 'pos' : 'neg'}>
                              (${variance > 0 ? '+' : ''}${variance})
                            </span>
                          `
                        : nothing
                    }
                  `
                : html`<span style="opacity: 0.6;"
                    >Not counted · expected ${line.expectedQty}</span
                  >`
            }
          </div>
        </div>
        <div class="row-status">
          ${
            flagged
              ? html`<sl-icon
                  name="exclamation-triangle-fill"
                  class="warn"
                ></sl-icon>`
              : counted != null
                ? html`<sl-icon
                    name="check-circle-fill"
                    class="check"
                  ></sl-icon>`
                : html`<sl-icon name="circle" class="empty"></sl-icon>`
          }
        </div>
      </button>
    `;
  }

  // ---------- Tablet/desktop dense table ----------
  private _renderTabletTable() {
    const groups = this._groupLinesByLocation();
    const usingLocations = this._locations.length > 0;
    const head = html`
      <div class="table-head">
        <div>Item</div>
        <div class="num">Expected</div>
        <div>Counted</div>
        <div class="num">Variance</div>
        <div class="num">Loss</div>
        <div>Anomaly</div>
      </div>
    `;
    const body = usingLocations
      ? html`${groups.map((g) => {
          const cnt = g.lines.filter((l) => l.counts.length > 0).length;
          return html`
            <header class="loc-header">
              <h3>${g.label}</h3>
              <span class="loc-rule"></span>
              <span class="loc-count"
                ><strong>${cnt}</strong> / ${g.lines.length}</span
              >
            </header>
            <div class="table-rows">
              ${g.lines.map((l) => this._renderTabletRow(l))}
            </div>
          `;
        })}`
      : html`<div class="table-rows">
          ${this._take?.lines.map((l) => this._renderTabletRow(l))}
        </div>`;
    return html`<sl-card>${head}${body}</sl-card>`;
  }

  private _renderTabletRow(line: IStockTakeLine) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    const expected = line.expectedQty;
    const counted =
      line.counts.length > 0 ? StocktakeEditPage._resolve(line.counts) : null;
    const variance = counted != null ? counted - expected : null;
    const lossValue =
      variance != null && variance < 0
        ? Math.abs(variance) * line.costSnapshot
        : 0;
    const inProgress = this._take?.status === 'in_progress';
    const flagged = line.anomalyFlags.length > 0;

    return html`
      <div class="table-row ${flagged ? 'flagged' : ''}">
        <div class="cell-item">
          ${item?.name ?? line.stockItemId}
          <small>${baseUnit}</small>
        </div>
        <div class="num">${expected}</div>
        <div>
          ${
            inProgress
              ? this._renderCountedCell(line)
              : html`<span class="num"
                  >${counted ?? '—'} ${counted != null ? baseUnit : ''}</span
                >`
          }
        </div>
        <div
          class="num ${variance == null ? '' : variance > 0 ? 'pos' : variance < 0 ? 'neg' : ''}"
        >
          ${variance == null ? '—' : variance > 0 ? `+${variance}` : variance}
        </div>
        <div class="num neg">${lossValue > 0 ? lossValue.toFixed(2) : ''}</div>
        <div class="anomaly-cell">
          ${line.anomalyFlags.map((flag) => {
            const meta = ANOMALY_LABEL[flag];
            return html`
              <sl-tooltip content=${meta.long}>
                <sl-tag variant="warning" size="small">${meta.short}</sl-tag>
              </sl-tooltip>
            `;
          })}
          ${
            flagged && inProgress
              ? html`
                  <sl-button
                    size="small"
                    variant="text"
                    @click=${() => this._openDetail(line.stockItemId)}
                  >
                    ${line.reconciliationExplanation ? 'Edit' : 'Explain'} →
                  </sl-button>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }

  // The Counted cell on tablet: single-bucket = inline qty + unit pill.
  // Multi-bucket = compact pill list + Edit button that opens the dialog
  // (where the multi-bucket UI lives properly without breaking the
  // single-row layout).
  private _renderCountedCell(line: IStockTakeLine) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';
    const buckets =
      this._bucketDrafts.get(line.stockItemId) ?? this._defaultBuckets(line);

    if (buckets.length > 1) {
      // Multi-bucket — read-only summary + Edit; the dialog covers editing.
      return html`
        <div class="counted-summary">
          <div class="summary-pills">
            ${buckets.map(
              (b) => html`
                <span class="pill">
                  ${b.quantity}×${b.unitLabel}${b.fillFraction < 1 ? ` @${Math.round(b.fillFraction * 100)}%` : ''}
                </span>
              `
            )}
          </div>
          <sl-button
            size="small"
            variant="text"
            @click=${() => this._openDetail(line.stockItemId)}
          >
            Edit
          </sl-button>
        </div>
      `;
    }

    // Single bucket — inline edit. Build the unit option list from baseUnit
    // + supplier purchase packs + any custom size already typed.
    const b = buckets[0] ?? {
      quantity: 0,
      unitSize: 1,
      unitLabel: baseUnit,
      fillFraction: 1,
      method: 'estimated' as const,
    };
    interface UnitOption {
      value: string;
      unitSize: number;
      unitLabel: string;
    }
    const unitOptions: UnitOption[] = [];
    const seen = new Set<string>();
    const push = (size: number, label: string) => {
      const key = `${size}:${label.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      unitOptions.push({ value: key, unitSize: size, unitLabel: label });
    };
    push(1, baseUnit);
    for (const link of item?.supplierLinks ?? []) {
      if (link.baseUnitsPerPurchaseUnit > 0 && link.purchaseUnit) {
        push(link.baseUnitsPerPurchaseUnit, link.purchaseUnit);
      }
    }
    push(b.unitSize, b.unitLabel);

    return html`
      <div class="counted-inline">
        <sl-input
          size="small"
          type="number"
          min="0"
          step="1"
          .value=${String(b.quantity)}
          @sl-input=${(e: Event) => {
            const v = Math.max(
              0,
              Math.floor(Number((e.target as HTMLInputElement).value) || 0)
            );
            this._setBucketField(line.stockItemId, 0, 'quantity', v);
          }}
          @sl-blur=${() => this._saveBuckets(line, this._bucketDrafts.get(line.stockItemId) ?? [])}
        ></sl-input>
        <sl-select
          size="small"
          .value=${`${b.unitSize}:${b.unitLabel.toLowerCase()}`}
          @sl-change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            const opt = unitOptions.find((o) => o.value === value);
            if (!opt) return;
            this._setBucketUnit(
              line.stockItemId,
              0,
              opt.unitSize,
              opt.unitLabel
            );
            void this._saveBuckets(
              line,
              this._bucketDrafts.get(line.stockItemId) ?? []
            );
          }}
        >
          ${unitOptions.map(
            (o) => html`<sl-option value=${o.value}>${o.unitLabel}</sl-option>`
          )}
        </sl-select>
        ${
          b.unitSize > 1
            ? html`
                <sl-input
                  class="fill"
                  size="small"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  .value=${String(b.fillFraction)}
                  title="Fill (0–1). 1 = sealed; 0.4 = ~40% full."
                  @sl-input=${(e: Event) => {
                    const raw = Number((e.target as HTMLInputElement).value);
                    const v = Math.max(
                      0,
                      Math.min(1, Number.isFinite(raw) ? raw : 1)
                    );
                    this._setBucketField(
                      line.stockItemId,
                      0,
                      'fillFraction',
                      v
                    );
                  }}
                  @sl-blur=${() => this._saveBuckets(line, this._bucketDrafts.get(line.stockItemId) ?? [])}
                ></sl-input>
              `
            : nothing
        }
        <sl-icon-button
          class="no-stock-btn"
          name="slash-circle"
          label="No stock"
          title="Mark zero counted"
          @click=${() => this._noStock(line)}
        ></sl-icon-button>
        <sl-icon-button
          name="plus-square"
          label="Add another size"
          title="Add another size (multi-bucket)"
          @click=${() => {
            this._addBucketRow(line);
            this._openDetail(line.stockItemId);
          }}
        ></sl-icon-button>
      </div>
    `;
  }

  // ---------- Detail dialog ----------
  // Always mounted; open/closed driven purely by _detailForId. Keeps the
  // element identity stable across open/close so Shoelace's transition
  // animates cleanly without a re-mount flicker.
  private _renderDetailDialog() {
    const id = this._detailForId;
    const line = id
      ? this._take?.lines.find((l) => l.stockItemId === id)
      : null;
    const item = line ? this._itemsById.get(line.stockItemId) : null;
    const baseUnit = item?.baseUnit ?? 'unit';
    const expected = line?.expectedQty ?? 0;
    const buckets = line
      ? (this._bucketDrafts.get(line.stockItemId) ?? this._defaultBuckets(line))
      : [];
    const savedResolved =
      line && line.counts.length > 0
        ? StocktakeEditPage._resolve(line.counts)
        : null;
    const variance = savedResolved != null ? savedResolved - expected : null;
    const flagged = line ? line.anomalyFlags.length > 0 : false;
    const inProgress = this._take?.status === 'in_progress';
    const locName =
      line && item?.locationId
        ? (this._locations.find((l) => l.id === item.locationId)?.name ?? null)
        : null;
    const expDraft = line ? (this._expDrafts.get(line.stockItemId) ?? '') : '';
    const hintDraft = line
      ? (this._hintDrafts.get(line.stockItemId) ?? '')
      : '';

    return html`
      <sl-dialog
        class="full-page-on-phone detail-dlg"
        label=${item?.name ?? ''}
        ?open=${line != null}
        @sl-request-close=${() => this._closeDetail()}
      >
        ${
          line == null
            ? nothing
            : html`
                <div class="detail-head">
                  ${locName ? html`<span class="loc-pill">${locName}</span>` : nothing}
                  <span class="expected"
                    >Expected <strong>${expected} ${baseUnit}</strong></span
                  >
                </div>

                <div class="detail-variance">
                  <div class="pair">
                    <span class="label">Counted</span>
                    <span class="value">
                      ${savedResolved ?? '—'}
                      ${savedResolved != null ? html`<small>${baseUnit}</small>` : nothing}
                    </span>
                  </div>
                  <div class="pair">
                    <span class="label">Variance</span>
                    <span
                      class="value ${variance == null ? '' : variance > 0 ? 'pos' : variance < 0 ? 'neg' : ''}"
                    >
                      ${variance == null ? '—' : variance > 0 ? `+${variance}` : variance}
                      ${variance != null ? html`<small>${baseUnit}</small>` : nothing}
                    </span>
                  </div>
                </div>

                ${inProgress ? this._renderDialogBucketEditor(line, buckets) : nothing}
                ${
                  flagged && inProgress
                    ? html`
                        <div class="recon">
                          <h4>This line needs an explanation</h4>
                          <div class="anomaly-cell">
                            ${line.anomalyFlags.map((flag) => {
                              const meta = ANOMALY_LABEL[flag];
                              return html`
                                <sl-tooltip content=${meta.long}>
                                  <sl-tag variant="warning" size="small"
                                    >${meta.short}</sl-tag
                                  >
                                </sl-tooltip>
                              `;
                            })}
                          </div>
                          <div class="recon-row">
                            <sl-select
                              size="small"
                              placeholder="Reason"
                              .value=${hintDraft}
                              @sl-change=${(e: Event) => {
                                const v = (e.target as HTMLSelectElement)
                                  .value as StockTakeReasonHint | '';
                                this._hintDrafts.set(line.stockItemId, v);
                                void this._saveReconciliation(
                                  line,
                                  expDraft,
                                  v
                                );
                              }}
                            >
                              ${REASON_HINTS.map(
                                (h) =>
                                  html`<sl-option value=${h.value}
                                    >${h.label}</sl-option
                                  >`
                              )}
                            </sl-select>
                            <sl-input
                              size="small"
                              placeholder="Explanation (required to complete)"
                              .value=${expDraft}
                              @sl-input=${(e: Event) => {
                                this._expDrafts.set(
                                  line.stockItemId,
                                  (e.target as HTMLInputElement).value
                                );
                                this.requestUpdate();
                              }}
                              @sl-blur=${(e: Event) =>
                                this._saveReconciliation(
                                  line,
                                  (e.target as HTMLInputElement).value,
                                  hintDraft
                                )}
                            ></sl-input>
                          </div>
                        </div>
                      `
                    : flagged && line.reconciliationExplanation
                      ? html`
                          <div class="recon">
                            <h4>Reconciliation</h4>
                            <p style="margin: 0; opacity: 0.85;">
                              ${line.reasonHint ? html`<strong>${line.reasonHint}:</strong> ` : nothing}
                              ${line.reconciliationExplanation}
                            </p>
                          </div>
                        `
                      : nothing
                }
                ${
                  line.originalCounts != null
                    ? html`
                        <p class="subtle" style="margin-top: 12px;">
                          First count:
                          ${StocktakeEditPage._resolve(line.originalCounts)}
                          ${baseUnit}
                        </p>
                      `
                    : nothing
                }

                <sl-button
                  slot="footer"
                  variant="default"
                  @click=${() => this._closeDetail()}
                >
                  Done
                </sl-button>
              `
        }
      </sl-dialog>
    `;
  }

  // Multi-bucket editor inside the detail dialog. Reuses the per-bucket
  // qty + unit picker + fill from the previous design, plus a No Stock
  // shortcut. Autosaves on blur / pick / chip tap.
  private _renderDialogBucketEditor(
    line: IStockTakeLine,
    buckets: ICountBucket[]
  ) {
    const item = this._itemsById.get(line.stockItemId);
    const baseUnit = item?.baseUnit ?? 'unit';

    interface UnitOption {
      value: string;
      unitSize: number;
      unitLabel: string;
    }
    const unitOptions: UnitOption[] = [];
    const seen = new Set<string>();
    const push = (size: number, label: string) => {
      const key = `${size}:${label.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      unitOptions.push({ value: key, unitSize: size, unitLabel: label });
    };
    push(1, baseUnit);
    for (const link of item?.supplierLinks ?? []) {
      if (link.baseUnitsPerPurchaseUnit > 0 && link.purchaseUnit) {
        push(link.baseUnitsPerPurchaseUnit, link.purchaseUnit);
      }
    }
    for (const b of buckets) push(b.unitSize, b.unitLabel);

    // When only the raw baseUnit is available (no supplier packs, no
    // historical custom sizes), the unit picker is a one-option dropdown —
    // wasted UI. Render a static label instead. The picker reappears the
    // moment any pack exists or a custom size is typed.
    const singleUnit = unitOptions.length === 1;

    return html`
      <div class="bucket-editor">
        ${buckets.map(
          (b, i) => html`
            <div class="bucket-row ${singleUnit ? 'single-unit' : ''}">
              <sl-input
                type="number"
                min="0"
                step="1"
                .value=${String(b.quantity)}
                @sl-input=${(e: Event) => {
                  const v = Math.max(
                    0,
                    Math.floor(
                      Number((e.target as HTMLInputElement).value) || 0
                    )
                  );
                  this._setBucketField(line.stockItemId, i, 'quantity', v);
                }}
                @sl-blur=${() => this._saveBuckets(line, this._bucketDrafts.get(line.stockItemId) ?? [])}
              ></sl-input>
              <span class="bucket-x">×</span>
              ${
                singleUnit
                  ? html`<span class="static-unit">${b.unitLabel}</span>`
                  : html`
                      <sl-select
                        .value=${`${b.unitSize}:${b.unitLabel.toLowerCase()}`}
                        @sl-change=${(e: Event) => {
                          const value = (e.target as HTMLSelectElement).value;
                          const opt = unitOptions.find(
                            (o) => o.value === value
                          );
                          if (!opt) return;
                          this._setBucketUnit(
                            line.stockItemId,
                            i,
                            opt.unitSize,
                            opt.unitLabel
                          );
                          void this._saveBuckets(
                            line,
                            this._bucketDrafts.get(line.stockItemId) ?? []
                          );
                        }}
                      >
                        ${unitOptions.map(
                          (o) =>
                            html`<sl-option value=${o.value}
                              >${o.unitLabel}${o.unitSize !== 1 ? ` (${o.unitSize} ${baseUnit})` : ''}</sl-option
                            >`
                        )}
                      </sl-select>
                    `
              }
              ${
                b.unitSize > 1
                  ? html`
                      <sl-input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        .value=${String(b.fillFraction)}
                        title="Fill (0–1). 1 = sealed; 0.4 = ~40% full."
                        @sl-input=${(e: Event) => {
                          const raw = Number(
                            (e.target as HTMLInputElement).value
                          );
                          const v = Math.max(
                            0,
                            Math.min(1, Number.isFinite(raw) ? raw : 1)
                          );
                          this._setBucketField(
                            line.stockItemId,
                            i,
                            'fillFraction',
                            v
                          );
                        }}
                        @sl-blur=${() => this._saveBuckets(line, this._bucketDrafts.get(line.stockItemId) ?? [])}
                      ></sl-input>
                    `
                  : html`<span></span>`
              }
              ${
                buckets.length > 1
                  ? html`
                      <sl-icon-button
                        name="trash"
                        label="Remove row"
                        @click=${() => {
                          this._removeBucketRow(line.stockItemId, i);
                          void this._saveBuckets(
                            line,
                            this._bucketDrafts.get(line.stockItemId) ?? []
                          );
                        }}
                      ></sl-icon-button>
                    `
                  : html`<span></span>`
              }
            </div>
          `
        )}
        <div class="bucket-actions">
          <sl-button
            size="small"
            variant="default"
            @click=${() => this._addBucketRow(line)}
          >
            <sl-icon slot="prefix" name="plus-square"></sl-icon>
            Add another size
          </sl-button>
          <sl-button
            size="small"
            variant="default"
            @click=${() => this._noStock(line)}
          >
            <sl-icon slot="prefix" name="slash-circle"></sl-icon>
            No stock
          </sl-button>
        </div>
      </div>
    `;
  }

  // ---------- Report view ----------
  private _renderReport() {
    if (!this._take) return nothing;
    const lines = this._take.lines;
    const counted = lines.filter((l) => l.counts.length > 0).length;
    const uncounted = lines.length - counted;
    let totalVar = 0;
    let totalLoss = 0;
    for (const l of lines) {
      if (l.counts.length === 0) continue;
      const c = StocktakeEditPage._resolve(l.counts);
      const v = c - l.expectedQty;
      totalVar += v;
      if (v < 0) totalLoss += Math.abs(v) * l.costSnapshot;
    }
    const unreconciled = this._unreconciledLines();
    const groups = this._groupLinesByLocation();

    return html`
      <div class="report-grid">
        <div class="stat">
          <div class="label">Counted</div>
          <div class="value">
            ${counted}
            <small style="font-size: 0.7em; opacity: 0.6;"
              >/ ${lines.length}</small
            >
          </div>
        </div>
        <div class="stat">
          <div class="label">Uncounted</div>
          <div class="value">${uncounted}</div>
        </div>
        <div class="stat">
          <div class="label">Net variance</div>
          <div
            class="value ${totalVar > 0 ? 'pos' : totalVar < 0 ? 'neg' : ''}"
          >
            ${totalVar > 0 ? '+' : ''}${totalVar.toFixed(0)}
          </div>
        </div>
        <div class="stat">
          <div class="label">Loss value</div>
          <div class="value neg">
            ${totalLoss > 0 ? totalLoss.toFixed(2) : '—'}
          </div>
        </div>
      </div>

      ${
        unreconciled.length > 0
          ? html`
              <div class="report-section">
                <h3>Lines needing explanation (${unreconciled.length})</h3>
                <div class="anomaly-list">
                  ${unreconciled.map((l) => {
                    const item = this._itemsById.get(l.stockItemId);
                    const c = StocktakeEditPage._resolve(l.counts);
                    const v = c - l.expectedQty;
                    return html`
                      <button
                        @click=${() => {
                          this._viewMode = 'list';
                          this._openDetail(l.stockItemId);
                        }}
                      >
                        <div>
                          <div class="a-name">
                            ${item?.name ?? l.stockItemId}
                          </div>
                          <div class="a-meta">
                            ${l.anomalyFlags.map((f) => ANOMALY_LABEL[f].short).join(' · ')}
                            · variance ${v > 0 ? '+' : ''}${v}
                            ${item?.baseUnit ?? ''}
                          </div>
                        </div>
                        <div class="a-cta">Explain →</div>
                      </button>
                    `;
                  })}
                </div>
              </div>
            `
          : nothing
      }
      ${
        this._locations.length > 0
          ? html`
              <div class="report-section">
                <h3>By location</h3>
                <div class="by-loc">
                  ${groups.map(
                    (g) => html`
                      <div class="by-loc-row">
                        <span>${g.label}</span>
                        <span>
                          <strong
                            >${g.lines.filter((l) => l.counts.length > 0).length}</strong
                          >
                          / ${g.lines.length}
                        </span>
                      </div>
                    `
                  )}
                </div>
              </div>
            `
          : nothing
      }
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    if (!this._take)
      return html`<sl-alert variant="danger" open
        >${this._error ?? 'Not found.'}</sl-alert
      >`;

    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');
    const counted = this._take.lines.filter((l) => l.counts.length > 0).length;
    const total = this._take.lines.length;
    const inProgress = this._take.status === 'in_progress';
    const unreconciledCount = this._unreconciledLines().length;
    const completeDisabled = this._busy || unreconciledCount > 0;

    return html`
      <header class="bar">
        <div class="header-meta">
          <h2>Stock take</h2>
          ${this._kindBadge(this._take.kind)}
          <sl-badge
            variant=${inProgress ? 'primary' : this._take.status === 'completed' ? 'success' : 'neutral'}
            pill
          >
            ${this._take.status.replace('_', ' ')}
          </sl-badge>
        </div>
      </header>
      <p class="subtle">
        Started ${new Date(this._take.startedUtc).toLocaleString()} · ${counted}
        / ${total} counted
      </p>

      ${
        unreconciledCount > 0
          ? html`
              <sl-alert variant="warning" open>
                <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                <strong>${unreconciledCount}</strong>
                ${unreconciledCount === 1 ? 'line needs' : 'lines need'} an
                explanation before this take can complete.
                <sl-button
                  slot="suffix"
                  size="small"
                  variant="text"
                  @click=${() => (this._viewMode = 'report')}
                >
                  View →
                </sl-button>
              </sl-alert>
            `
          : nothing
      }
      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}

      <sl-tab-group
        class="view-tabs"
        @sl-tab-show=${(e: CustomEvent<{ name: string }>) => {
          this._viewMode = e.detail.name === 'report' ? 'report' : 'list';
        }}
      >
        <sl-tab slot="nav" panel="list" ?active=${this._viewMode === 'list'}
          >List</sl-tab
        >
        <sl-tab slot="nav" panel="report" ?active=${this._viewMode === 'report'}
          >Report</sl-tab
        >
        <sl-tab-panel name="list">
          ${this._isPhone ? this._renderPhoneList() : this._renderTabletTable()}
        </sl-tab-panel>
        <sl-tab-panel name="report">${this._renderReport()}</sl-tab-panel>
      </sl-tab-group>

      ${
        this._summary
          ? html`
              <sl-card class="summary">
                <strong>Reconciled</strong>
                <div class="report-grid">
                  <div class="stat">
                    <div class="label">Lines counted</div>
                    <div class="value">${this._summary.linesCounted}</div>
                  </div>
                  <div class="stat">
                    <div class="label">Lines uncounted</div>
                    <div class="value">${this._summary.linesUncounted}</div>
                  </div>
                  <div class="stat">
                    <div class="label">Total adjustment</div>
                    <div class="value">${this._summary.totalAdjustments}</div>
                  </div>
                  <div class="stat">
                    <div class="label">Loss value</div>
                    <div class="value neg">
                      ${this._summary.totalLossValue.toFixed(2)}
                    </div>
                  </div>
                </div>
              </sl-card>
            `
          : nothing
      }
      ${
        this._lineFailures.length > 0
          ? html`
              <sl-alert variant="warning" open>
                <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                <strong>
                  ${this._lineFailures.length}
                  ${this._lineFailures.length === 1 ? 'line did' : 'lines did'}
                  not fully reconcile.
                </strong>
                <ul class="line-failures">
                  ${this._lineFailures.map(
                    (f) => html`
                      <li>
                        <strong
                          >${this._itemsById.get(f.stockItemId)?.name ?? f.stockItemId}</strong
                        >
                        <span class="line-failure-kind"
                          >${this._failureKindLabel(f.kind)}</span
                        >
                        <div class="line-failure-message">${f.message}</div>
                      </li>
                    `
                  )}
                </ul>
              </sl-alert>
            `
          : nothing
      }
      ${this._renderDetailDialog()}

      <page-action-bar>
        <sl-button slot="left" variant="default" @click=${this._back}
          >Back</sl-button
        >
        ${
          inProgress && isOwner
            ? html`
                <sl-button
                  slot="right"
                  variant="warning"
                  @click=${this._cancel}
                  ?disabled=${this._busy}
                >
                  Cancel take
                </sl-button>
                <sl-button
                  slot="right"
                  variant="primary"
                  @click=${this._complete}
                  ?loading=${this._busy}
                  ?disabled=${completeDisabled}
                >
                  Complete & reconcile
                </sl-button>
              `
            : nothing
        }
      </page-action-bar>
    `;
  }
}

function readTakeIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/stocktake\/([^/?#]+)/);
  if (!m) return null;
  if (m[1] === 'new') return null;
  return decodeURIComponent(m[1]);
}
